// File: frontend/src/services/apiClient.ts
/**
 * API Client with automatic Authorization header injection
 * 
 * This module provides a centralized HTTP client that:
 * - Automatically adds JWT token to all requests
 * - Handles 401 responses by clearing auth state
 * - Handles 429 responses for rate limiting
 * - Provides consistent error handling
 */

import { API_BASE_URL } from "../constants";

// Callback function to handle unauthorized responses (set by AuthContext)
let onUnauthorized: (() => void) | null = null;

/**
 * Custom error class for rate limit exceeded errors (HTTP 429)
 */
export class RateLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RateLimitError";
  }
}

/**
 * Custom error class for non-canonical slugs (HTTP 301).
 *
 * Raised when a page is requested by a URL that resolves correctly but is not
 * its canonical address — an older link shared before the "--{id}" suffix
 * existed, or one whose readable part went stale after a title was corrected.
 * The backend still returns the topic's identity in redirectPath; pages catch
 * this and navigate there with { replace: true } so the address bar and search
 * engines converge on one URL per topic.
 *
 * Deliberately separate from MergedTopicError: 410 means the topic no longer
 * exists on its own, 301 means same topic at a better address. Both carry a
 * redirectPath and both are handled identically by pages, which is why they
 * share the RedirectError base class.
 */
export class RedirectError extends Error {
  redirectPath: string;

  constructor(redirectPath: string, name: string, message: string) {
    super(message);
    this.name = name;
    this.redirectPath = redirectPath;
  }
}

/**
 * Custom error class for merged topics (HTTP 410).
 * redirectPath is the full frontend route of the surviving topic —
 * pages catch this error and navigate there.
 */
export class MergedTopicError extends RedirectError {
  constructor(redirectPath: string) {
    super(redirectPath, "MergedTopicError", "Topic was merged");
  }
}

/**
 * Non-canonical slug — see RedirectError above.
 */
export class SlugRedirectError extends RedirectError {
  constructor(redirectPath: string) {
    super(redirectPath, "SlugRedirectError", "Non-canonical slug");
  }
}

/**
 * Custom error class for duplicate entry errors (HTTP 409)
 */
export class DuplicateEntryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DuplicateEntryError";
  }
}

/**
 * Custom error class for connectivity failures.
 *
 * fetch() rejects with a bare TypeError when the request never reached the
 * server at all — no connection, DNS failure, server unreachable, request
 * blocked. Without this distinction every page showed its own generic
 * "could not load" text, which told the user nothing about what to do.
 */
export class NetworkError extends Error {
  constructor(message = "NETWORK_ERROR") {
    super(message);
    this.name = "NetworkError";
  }
}

/**
 * Custom error class for 5xx responses — the request DID reach the server,
 * the server failed. Distinct from NetworkError because the user action is
 * different: retrying in a moment usually helps, checking the wifi does not.
 */
export class ServerError extends Error {
  status: number;

  constructor(status: number, message = "SERVER_ERROR") {
    super(message);
    this.name = "ServerError";
    this.status = status;
  }
}

/**
 * Notified whenever a request fails at the network level (set by
 * ConnectivityContext). navigator.onLine alone is not trustworthy — it only
 * reports whether an interface is up, not whether the internet is reachable,
 * so an actual failed request is the stronger signal.
 */
let onNetworkFailure: (() => void) | null = null;

/** Notified whenever a request succeeds — used to clear the offline state. */
let onNetworkSuccess: (() => void) | null = null;

export const setNetworkStatusCallbacks = (
  onFailure: (() => void) | null,
  onSuccess: (() => void) | null
): void => {
  onNetworkFailure = onFailure;
  onNetworkSuccess = onSuccess;
};

/**
 * Wraps a fetch call so a connectivity failure surfaces as NetworkError
 * instead of an opaque TypeError, and so the connectivity listeners are
 * notified either way.
 */
const request = async (input: string, init: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(input, init);
    onNetworkSuccess?.();
    return response;
  } catch (err) {
    // AbortError is a deliberate cancellation, not a connectivity problem
    if ((err as Error)?.name === "AbortError") {
      throw err;
    }
    onNetworkFailure?.();
    throw new NetworkError();
  }
};

/**
 * Set the callback function to be called on 401 Unauthorized responses
 * This is called by AuthContext to wire up the logout functionality
 */
export const setUnauthorizedCallback = (callback: () => void): void => {
  onUnauthorized = callback;
};

/**
 * Get authorization headers if token exists
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }
  
  return {};
};

/**
 * Handle response and check for unauthorized status
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    
    // Trigger logout callback if set
    if (onUnauthorized) {
      onUnauthorized();
    }
    
    throw new Error("Session expired. Please login again.");
  }

  // Handle 429 Too Many Requests - rate limit exceeded
  if (response.status === 429) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || "Too many requests. Please wait and try again.";
    throw new RateLimitError(errorMessage);
  }

  // Handle 409 Conflict - duplicate entry
  if (response.status === 409) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || "Duplicate entry.";
    throw new DuplicateEntryError(errorMessage);
  }

  // Handle 301 Moved Permanently - slug resolved, but not at its canonical URL.
  //
  // fetch() cannot follow this one: the backend answers with a JSON body and no
  // Location header (the target is a frontend route, not an API endpoint), and
  // per the Fetch spec a redirect status without Location is handed back to the
  // caller untouched. So the status lands here and the page navigates itself.
  if (response.status === 301) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.redirectPath) {
      throw new SlugRedirectError(errorData.redirectPath);
    }
    throw new Error("Redirect without target.");
  }

  // Handle 410 Gone - topic was merged into another topic
  if (response.status === 410) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.redirectPath) {
      throw new MergedTopicError(errorData.redirectPath);
    }
    throw new Error("Content is gone.");
  }

  // Handle 5xx — server-side failure, worth retrying shortly
  if (response.status >= 500) {
    const errorData = await response.json().catch(() => ({}));
    throw new ServerError(response.status, errorData.message || "SERVER_ERROR");
  }

  // Handle other error responses
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = 
      errorData.message || 
      errorData.error || 
      `Request failed with status: ${response.status}`;
    
    throw new Error(errorMessage);
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
};

/**
 * API Client object with HTTP methods
 */
export const apiClient = {
  /**
   * GET request
   */
  get: async <T>(endpoint: string): Promise<T> => {
    const response = await request(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    });

    return handleResponse<T>(response);
  },

  /**
   * POST request
   */
  post: async <T>(endpoint: string, data?: unknown): Promise<T> => {
    const response = await request(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  },

  /**
   * PUT request
   */
  put: async <T>(endpoint: string, data?: unknown): Promise<T> => {
    const response = await request(`${API_BASE_URL}${endpoint}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  },

  /**
   * PATCH request
   */
  patch: async <T>(endpoint: string, data?: unknown): Promise<T> => {
    const response = await request(`${API_BASE_URL}${endpoint}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  },

  /**
   * DELETE request — optionally accepts a JSON body (e.g. for delete-with-options endpoints)
   */
  delete: async <T>(endpoint: string, body?: unknown): Promise<T> => {
    const response = await request(`${API_BASE_URL}${endpoint}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    return handleResponse<T>(response);
  },
};

export default apiClient;